import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import {
  StoredUserPreferencesSchema,
  ThemePreference,
  type UserPreferences,
  UserPreferencesSchema,
} from "@recobra/schemas/user";

import { profileApi } from "./api";
import { authQueryOptions } from "./authQuery";

const CACHE_STORAGE_KEY = "recobra-preferences";

export const DEFAULT_THEME: ThemePreference = "soft";

const THEME_STORAGE_KEY = "recobra-theme";

const tryStoreLocally = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

/** Applies the theme and remembers it on this device, so the next load paints with it before sign-in resolves. */
export const applyTheme = (theme: ThemePreference = DEFAULT_THEME) => {
  document.documentElement.dataset.theme = theme;
  tryStoreLocally(THEME_STORAGE_KEY, theme);
};

export const lastAppliedTheme = (): ThemePreference => {
  try {
    return ThemePreference.catch(DEFAULT_THEME).parse(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
};

export const readCachedPreferences = (username: string): UserPreferences => {
  try {
    const cached = localStorage.getItem(`${CACHE_STORAGE_KEY}:${username}`);
    return StoredUserPreferencesSchema.catch({}).parse(JSON.parse(cached ?? "{}"));
  } catch {
    return {};
  }
};

/**
 * The signed-in user's UI preferences: the last server state with every
 * pending patch applied on top, in submission order. Patches are sent one at
 * a time; each response replaces the server state, and a failed patch simply
 * stops being applied, so only its own keys revert.
 */
export const useUserPreferences = () => {
  const { data: auth } = useSuspenseQuery(authQueryOptions);
  const username = auth.user.username;
  const userPreferencesQueryKey = useMemo(
    () => ["profile", "preferences", username] as const,
    [username],
  );
  const queryClient = useQueryClient();
  const { data: stored } = useQuery({
    queryKey: userPreferencesQueryKey,
    queryFn: ({ signal }) => profileApi.getPreferences(signal),
    initialData: () => readCachedPreferences(username),
    initialDataUpdatedAt: 0,
  });
  const pendingPatches = useMutationState({
    filters: { mutationKey: userPreferencesQueryKey, status: "pending", exact: true },
    select: (mutation) => UserPreferencesSchema.parse(mutation.state.variables),
  });
  const preferences = useMemo(
    () =>
      pendingPatches.reduce<UserPreferences>(
        (merged, patch) => ({ ...merged, ...patch }),
        stored ?? {},
      ),
    [stored, pendingPatches],
  );

  useEffect(
    () => tryStoreLocally(`${CACHE_STORAGE_KEY}:${username}`, JSON.stringify(preferences)),
    [username, preferences],
  );

  const { mutate: updatePreferences } = useMutation({
    mutationKey: userPreferencesQueryKey,
    scope: { id: `profile-preferences:${username}` },
    mutationFn: profileApi.updatePreferences,
    onSuccess: async (saved) => {
      await queryClient.cancelQueries({ queryKey: userPreferencesQueryKey });
      queryClient.setQueryData(userPreferencesQueryKey, saved);
    },
    onError: () => queryClient.invalidateQueries({ queryKey: userPreferencesQueryKey }),
  });

  return { preferences, updatePreferences };
};
